import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2, Plus, ArrowUpDown, Search, History, RotateCcw, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { OperationsHistoryModal } from "@/v2/components/common/OperationsHistoryModal";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useWorkflows } from "@/hooks/workflow/useWorkflows";
import { useTasks } from "@/hooks/workflow/useTasks";
import { useWorkflowStatuses } from "@/hooks/workflow/useWorkflowStatuses";
import { useWorkflowAuditLog } from "@/hooks/workflow/useWorkflowAuditLog";
import { useWorkflowStatusAuditLog } from "@/hooks/workflow/useWorkflowStatusAuditLog";
import { useTaskAuditLog } from "@/hooks/workflow/useTaskAuditLog";
import { useUserGroupsList } from "@/hooks/users/useUserGroupsList";
import LoaderComponent from "@/components/common/LoaderComponent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/services/core/apiClient";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";

// Mock data for workflows
const mockWorkflows = [
  {
    id: "1",
    workflowName: "Patient Onboarding Flow",
    applicablePrograms: ["Diabetes Management", "Hypertension Control"],
    userType: "VPE",
    createdDate: "2024-01-15",
    updatedDate: "2024-02-20"
  },
  {
    id: "2", 
    workflowName: "Provider Assessment Workflow",
    applicablePrograms: ["Cardiac Rehabilitation", "Weight Loss Program"],
    userType: "CS",
    createdDate: "2024-01-10",
    updatedDate: "2024-02-18"
  },
  {
    id: "3",
    workflowName: "Care Coordinator Task Flow", 
    applicablePrograms: ["Diabetes Management"],
    userType: "VPE",
    createdDate: "2024-01-20",
    updatedDate: "2024-02-22"
  },
  {
    id: "4",
    workflowName: "Admin Review Process",
    applicablePrograms: ["Hypertension Control", "Cardiac Rehabilitation", "Weight Loss Program"],
    userType: "PES",
    createdDate: "2024-01-25",
    updatedDate: "2024-02-25"
  },
  {
    id: "5",
    workflowName: "Patient Follow-up Sequence",
    applicablePrograms: ["Diabetes Management", "Hypertension Control"],
    userType: "CS",
    createdDate: "2024-02-01",
    updatedDate: "2024-02-28"
  }
];


const Workflow = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, token } = useAuth();
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>(null);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "workflow");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedEntityForHistory, setSelectedEntityForHistory] = useState<any>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null);
  const [selectedWorkflowStatusId, setSelectedWorkflowStatusId] = useState<number | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedUserGroups, setSelectedUserGroups] = useState<number[]>([]);
  const [userGroupPopoverOpen, setUserGroupPopoverOpen] = useState(false);
  
  // Fetch user groups for display
  const { userGroups, loading: loadingUserGroups } = useUserGroupsList();
  
  // Helper function to get user group name from ID
  const getUserGroupName = (groupId?: number) => {
    if (!groupId) return 'Not Assigned';
    const group = userGroups.find(g => g.user_group_id === groupId);
    return group?.user_group_name || 'Not Assigned';
  };

  // Use workflows hook for API integration
  const {
    workflows,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch: performSearch,
    clearSearch
  } = useWorkflows();

  // Use tasks hook for API integration
  const {
    tasks: apiTasks,
    loading: tasksLoading,
    error: tasksError,
    currentPage: tasksCurrentPage,
    totalPages: tasksTotalPages,
    totalCount: tasksTotalCount,
    entriesPerPage: tasksEntriesPerPage,
    searchValue: tasksSearchValue,
    setCurrentPage: setTasksCurrentPage,
    setEntriesPerPage: setTasksEntriesPerPage,
    setSearchValue: setTasksSearchValue,
    handleSearch: performTasksSearch,
    clearSearch: clearTasksSearch
  } = useTasks();

  // Use workflow statuses hook for API integration
  const {
    workflowStatuses: apiWorkflowStatuses,
    loading: statusesLoading,
    error: statusesError,
    currentPage: statusesCurrentPage,
    totalPages: statusesTotalPages,
    totalCount: statusesTotalCount,
    entriesPerPage: statusesEntriesPerPage,
    searchValue: statusesSearchValue,
    setCurrentPage: setStatusesCurrentPage,
    setEntriesPerPage: setStatusesEntriesPerPage,
    setSearchValue: setStatusesSearchValue,
    handleSearch: performStatusesSearch,
    clearSearch: clearStatusesSearch
  } = useWorkflowStatuses();

  // Fetch workflow audit logs when a workflow is selected
  const { auditLogs: workflowAuditLogs, loading: auditLogsLoading } = useWorkflowAuditLog(
    activeTab === "workflow" ? selectedWorkflowId : null
  );
  
  // Fetch workflow status audit logs when a workflow status is selected
  const { workflowStatusAuditLogs, auditLogsLoading: workflowStatusAuditLogsLoading } = useWorkflowStatusAuditLog(
    activeTab === "workflow-status" ? selectedWorkflowStatusId : null
  );
  
  // Fetch task audit logs when a task is selected
  const { taskAuditLogs, auditLogsLoading: taskAuditLogsLoading } = useTaskAuditLog(
    activeTab === "tasks" ? selectedTaskId : null
  );

  useEffect(() => {
    document.title = "Workflow & Tasks | Admin";
  }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["workflow", "workflow-status", "tasks"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const handleEdit = (id: number | string, item?: any) => {
    if (activeTab === "workflow") {
      navigate(`/admin/workflow/edit/${id}`);
    } else if (activeTab === "workflow-status") {
      navigate(`/admin/workflow-status/edit/${id}`, {
        state: {
          id: item?.workflow_status_id,
          workflowName: item?.workflow_status
        }
      });
    } else if (activeTab === "tasks") {
      navigate(`/admin/tasks/edit/${id}`);
    }
  };

  const handleDeleteClick = (id: number | string) => {
    setDeleteItemId(id);
    setIsDeleteDialogOpen(true);
  };

  // Guard against Radix auto-closing the dialog while an async delete is in-flight.
  // This makes the loader reliably visible until we explicitly close.
  const handleDeleteDialogOpenChange = (open: boolean) => {
    if (!open && isDeleting) return;
    setIsDeleteDialogOpen(open);
  };

  const handleDelete = async () => {
    if (deleteItemId == null || !user || !token) {
      toast({
        title: "Error",
        description: "Unable to delete. Please log in again and retry.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);

    // Ensure the UI renders the loading state before the request starts
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    try {
      if (activeTab === "workflow-status") {
        const endpoint = `/api/ngpep/${user.id}/workflowStatus/${deleteItemId}`;
        const response = await apiClient.delete(endpoint, token);

        if (response.success) {
          toast({
            title: "Success",
            description: "Workflow Status has been successfully deleted.",
          });
          
          // Refresh the workflow statuses list
          window.location.reload();
        } else {
          throw new Error(response.error || "Failed to delete workflow status");
        }
      } else if (activeTab === "workflow") {
        const endpoint = `/api/ngpep/${user.id}/workflow/${deleteItemId}`;
        const response = await apiClient.delete(endpoint, token);

        if (response.success) {
          toast({
            title: "Success",
            description: "Workflow has been successfully deleted.",
          });
          
          // Refresh the workflows list
          window.location.reload();
        } else {
          throw new Error(response.error || "Failed to delete workflow");
        }
      } else if (activeTab === "tasks") {
        const endpoint = `/api/ngpep/${user.id}/taskstatus/${deleteItemId}`;
        const response = await apiClient.delete(endpoint, token);

        if (response.success) {
          toast({
            title: "Success",
            description: "Task has been successfully deleted.",
          });
          
          // Refresh the tasks list
          window.location.reload();
        } else {
          throw new Error(response.error || "Failed to delete task");
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDeleteItemId(null);
    }
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleHistory = (entity: any) => {
    setSelectedEntityForHistory(entity);
    if (activeTab === "workflow" && entity.workflow_id) {
      setSelectedWorkflowId(entity.workflow_id);
      setSelectedWorkflowStatusId(null);
      setSelectedTaskId(null);
    } else if (activeTab === "workflow-status" && entity.workflow_status_id) {
      setSelectedWorkflowStatusId(entity.workflow_status_id);
      setSelectedWorkflowId(null);
      setSelectedTaskId(null);
    } else if (activeTab === "tasks" && entity.task_id) {
      setSelectedTaskId(entity.task_id);
      setSelectedWorkflowId(null);
      setSelectedWorkflowStatusId(null);
    } else {
      setSelectedWorkflowId(null);
      setSelectedWorkflowStatusId(null);
      setSelectedTaskId(null);
    }
    setIsHistoryModalOpen(true);
  };

  const handleAddNew = () => {
    if (activeTab === "workflow") {
      navigate("/admin/workflow/add");
    } else if (activeTab === "workflow-status") {
      navigate("/admin/workflow-status/add");
    } else if (activeTab === "tasks") {
      navigate("/admin/tasks/add");
    }
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSearchClick = () => {
    if (filterType === 'assigned_user_group') {
      if (selectedUserGroups.length === 0) {
        toast({
          title: "Error",
          description: "Please select at least one user group",
          variant: "destructive"
        });
        return;
      }
      // Pass the array as JSON string and trigger search after state update
      const searchVal = JSON.stringify(selectedUserGroups);
      setSearchValue(searchVal);
      // Use setTimeout to ensure state update completes before searching
      setTimeout(() => performSearch(), 0);
    } else {
      if (!searchValue.trim()) {
        clearSearch();
        return;
      }
      performSearch();
    }
  };

  const toggleUserGroup = (userGroupId: number) => {
    setSelectedUserGroups(prev => 
      prev.includes(userGroupId)
        ? prev.filter(id => id !== userGroupId)
        : [...prev, userGroupId]
    );
  };

  const sortedWorkflows = useMemo(() => {
    if (!sortConfig) return workflows;

    return [...workflows].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortConfig.key === 'workflow_name') {
        aValue = a.workflow_name;
        bValue = b.workflow_name;
      } else if (sortConfig.key === 'program_type') {
        aValue = a.program_type?.join(', ') || '';
        bValue = b.program_type?.join(', ') || '';
      } else if (sortConfig.key === 'assigned_user_type') {
        aValue = a.assigned_user_type;
        bValue = b.assigned_user_type;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [workflows, sortConfig]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getButtonLabel = () => {
    switch (activeTab) {
      case "workflow":
        return "Create New Workflow";
      case "workflow-status":
        return "Add New Workflow Status";
      case "tasks":
        return "Add New Task";
      default:
        return "Add New";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Workflow & Tasks Management</h1>
          <p className="text-muted-foreground mt-2">Design and manage workflows, workflow statuses, and tasks</p>
        </div>
        <Button onClick={handleAddNew} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6">
          <Plus className="h-4 w-4" />
          {getButtonLabel()}
        </Button>
      </div>

      <Tabs defaultValue="workflow" value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex items-center justify-between">
          <TabsList className="grid w-fit grid-cols-3 bg-transparent p-0 h-auto">
            <TabsTrigger 
              value="workflow" 
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6"
            >
              Workflow
            </TabsTrigger>
            <TabsTrigger 
              value="workflow-status"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6"
            >
              Workflow Status
            </TabsTrigger>
            <TabsTrigger 
              value="tasks"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6"
            >
              Tasks
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="workflow" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Select 
                value={filterType} 
                onValueChange={(value) => {
                  setFilterType(value);
                  setSearchValue('');
                  setSelectedUserGroups([]);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Search by..." />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background border shadow-md">
                  <SelectItem value="workflow_name">Workflow Name</SelectItem>
                  <SelectItem value="program_type">Applicable Programs</SelectItem>
                  <SelectItem value="assigned_user_group">Assigned User Group</SelectItem>
                </SelectContent>
              </Select>
              
              {filterType === 'assigned_user_group' ? (
                <Popover open={userGroupPopoverOpen} onOpenChange={setUserGroupPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={userGroupPopoverOpen}
                      className="w-[400px] justify-between h-auto min-h-[40px] py-2"
                    >
                      {selectedUserGroups.length === 0 ? (
                        'Select user groups...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {selectedUserGroups.map(id => {
                            const group = userGroups.find(g => g.user_group_id === id);
                            return (
                              <Badge key={id} variant="secondary" className="text-xs">
                                {group?.user_group_name || `ID: ${id}`}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 z-50 bg-background" align="start">
                    <Command>
                      <CommandInput placeholder="Search user groups..." />
                      <CommandEmpty>No user group found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {loadingUserGroups ? (
                          <div className="py-6 text-center text-sm">Loading...</div>
                        ) : (
                          userGroups.map((group) => (
                            <CommandItem
                              key={group.user_group_id}
                              value={group.user_group_name}
                              onSelect={() => toggleUserGroup(group.user_group_id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedUserGroups.includes(group.user_group_id) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{group.user_group_name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {group.user_count} user{group.user_count !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </CommandItem>
                          ))
                        )}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <Input 
                  className="w-64" 
                  placeholder={
                    filterType === 'workflow_name' ? 'Enter workflow name...' :
                    filterType === 'program_type' ? 'Enter program type...' :
                    'Enter search value...'
                  }
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
                />
              )}
              <Button onClick={handleSearchClick} className="bg-purple-600 hover:bg-purple-700 text-white px-8">
                <Search className="h-4 w-4" />
                Search
              </Button>
              {(searchValue || selectedUserGroups.length > 0) && (
                <Button 
                  onClick={() => {
                    setSearchValue('');
                    setSelectedUserGroups([]);
                    clearSearch();
                  }}
                  variant="outline"
                  className="px-6"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select value={entriesPerPage.toString()} onValueChange={(val) => setEntriesPerPage(parseInt(val))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background border shadow-md">
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">Entries</span>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-8">
              <LoaderComponent />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-destructive">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>

            <div id="table-admin-workflows-container" className="rounded-md border">
              <Table id="table-admin-workflows">
                <TableHeader>
                  <TableRow id="table-admin-workflows-header-row">
                    <TableHead 
                      id="header-cell-workflow-name"
                      className="cursor-pointer hover:bg-muted/50" 
                      onClick={() => handleSort('workflow_name')}
                    >
                      <div className="flex items-center gap-2">
                        Workflow Name
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead 
                      id="header-cell-applicable-programs"
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('program_type')}
                    >
                      <div className="flex items-center gap-2">
                        Applicable Programs
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead 
                      id="header-cell-assigned-user-group"
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('assigned_user_group_id')}
                    >
                      <div className="flex items-center gap-2">
                        Assigned User Group
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody id="table-admin-workflows-body">
                  {sortedWorkflows.map((workflow) => (
                    <TableRow key={workflow.workflow_id} id={`row-workflow-${workflow.workflow_id}`}>
                      <TableCell id={`cell-workflow-${workflow.workflow_id}-name`} className="font-medium">
                        {workflow.workflow_name}
                      </TableCell>
                      <TableCell id={`cell-workflow-${workflow.workflow_id}-programs`}>
                        <div className="flex flex-wrap gap-1">
                          {expandedRows.has(workflow.workflow_id)
                            ? workflow.program_type?.map((program, index) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {program}
                                </Badge>
                              ))
                            : workflow.program_type?.slice(0, 3).map((program, index) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {program}
                                </Badge>
                              ))
                          }
                          {workflow.program_type && workflow.program_type.length > 3 && (
                            <Badge 
                              variant="outline" 
                              className="text-xs cursor-pointer hover:bg-accent"
                              onClick={() => toggleRowExpansion(workflow.workflow_id)}
                            >
                              {expandedRows.has(workflow.workflow_id)
                                ? 'Show less'
                                : `+${workflow.program_type.length - 3}`
                              }
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell id={`cell-workflow-${workflow.workflow_id}-user-group`}>
                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          {getUserGroupName(workflow.assigned_user_group_id)}
                        </Badge>
                      </TableCell>
                      <TableCell id={`cell-workflow-${workflow.workflow_id}-actions`} className="text-right">
                        <div className="flex justify-end gap-2">
                          <IconButtonWithTooltip
                            id={`btn-workflow-${workflow.workflow_id}-history`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleHistory(workflow)}
                            tooltip="View History"
                            icon={<History className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-workflow-${workflow.workflow_id}-edit`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(workflow.workflow_id)}
                            tooltip="Edit"
                            icon={<Edit className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-workflow-${workflow.workflow_id}-delete`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(workflow.workflow_id)}
                            className="text-destructive hover:text-destructive"
                            tooltip="Delete"
                            icon={<Trash2 className="h-4 w-4" />}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {sortedWorkflows.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No workflows found.</p>
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground text whitespace-nowrap">
                Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalCount)} of {totalCount} entries
              </div>
              
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink 
                          onClick={() => setCurrentPage(pageNum)}
                          className={pageNum === currentPage 
                            ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                            : "hover:bg-purple-100 cursor-pointer"
                          }
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </>
          )}
        </TabsContent>

        <TabsContent value="workflow-status" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Input 
                className="w-64" 
                placeholder="Search by Workflow Status..."
                value={statusesSearchValue}
                onChange={(e) => {
                  setStatusesSearchValue(e.target.value);
                  if (!e.target.value.trim()) {
                    clearStatusesSearch();
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && performStatusesSearch()}
              />
              <Button 
                onClick={performStatusesSearch}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                disabled={!statusesSearchValue.trim()}
              >
                <Search className="h-4 w-4" />
                Search
              </Button>
              {statusesSearchValue && (
                <Button 
                  onClick={clearStatusesSearch}
                  variant="outline"
                  className="px-6"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select value={statusesEntriesPerPage.toString()} onValueChange={(val) => setStatusesEntriesPerPage(parseInt(val))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background border shadow-md">
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">Entries</span>
            </div>
          </div>

          {statusesLoading && (
            <div className="flex justify-center py-8">
              <LoaderComponent />
            </div>
          )}

          {statusesError && (
            <div className="text-center py-8">
              <p className="text-destructive">{statusesError}</p>
            </div>
          )}

          {!statusesLoading && !statusesError && (
            <>
              <div id="table-admin-workflow-statuses-container" className="rounded-md border">
                <Table id="table-admin-workflow-statuses">
                  <TableHeader>
                    <TableRow id="table-admin-workflow-statuses-header-row">
                      <TableHead id="header-cell-status-name">Workflow Status Name</TableHead>
                      <TableHead id="header-cell-created-date">Created Date</TableHead>
                      <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody id="table-admin-workflow-statuses-body">
                    {apiWorkflowStatuses.map((workflowStatus) => (
                      <TableRow key={workflowStatus.workflow_status_id} id={`row-workflow-status-${workflowStatus.workflow_status_id}`}>
                        <TableCell id={`cell-workflow-status-${workflowStatus.workflow_status_id}-name`} className="font-medium">
                          {workflowStatus.workflow_status}
                        </TableCell>
                        <TableCell id={`cell-workflow-status-${workflowStatus.workflow_status_id}-created`}>{formatDate(workflowStatus.created_datetime)}</TableCell>
                        <TableCell id={`cell-workflow-status-${workflowStatus.workflow_status_id}-actions`} className="text-right">
                          <div className="flex justify-end gap-2">
                            <IconButtonWithTooltip
                              id={`btn-workflow-status-${workflowStatus.workflow_status_id}-history`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleHistory(workflowStatus)}
                              tooltip="View History"
                              icon={<History className="h-4 w-4" />}
                            />
                            <IconButtonWithTooltip
                              id={`btn-workflow-status-${workflowStatus.workflow_status_id}-edit`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(workflowStatus.workflow_status_id, workflowStatus)}
                              tooltip="Edit"
                              icon={<Edit className="h-4 w-4" />}
                            />
                            <IconButtonWithTooltip
                              id={`btn-workflow-status-${workflowStatus.workflow_status_id}-delete`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(workflowStatus.workflow_status_id)}
                              className="text-destructive hover:text-destructive"
                              tooltip="Delete"
                              icon={<Trash2 className="h-4 w-4" />}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {apiWorkflowStatuses.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No workflow statuses found.</p>
                </div>
              )}

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground text whitespace-nowrap">
                  Showing {((statusesCurrentPage - 1) * statusesEntriesPerPage) + 1} to {Math.min(statusesCurrentPage * statusesEntriesPerPage, statusesTotalCount)} of {statusesTotalCount} entries
                </div>
                
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setStatusesCurrentPage(statusesCurrentPage - 1)}
                        className={statusesCurrentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    {Array.from({ length: Math.min(5, statusesTotalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink 
                            onClick={() => setStatusesCurrentPage(pageNum)}
                            className={pageNum === statusesCurrentPage 
                              ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                              : "hover:bg-purple-100 cursor-pointer"
                            }
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setStatusesCurrentPage(statusesCurrentPage + 1)}
                        className={statusesCurrentPage === statusesTotalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Input 
                className="w-64" 
                placeholder="Search by Task Name..."
                value={tasksSearchValue}
                onChange={(e) => {
                  setTasksSearchValue(e.target.value);
                  if (!e.target.value.trim()) {
                    clearTasksSearch();
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && performTasksSearch()}
              />
              <Button 
                onClick={performTasksSearch}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                disabled={!tasksSearchValue.trim()}
              >
                <Search className="h-4 w-4" />
                Search
              </Button>
              {tasksSearchValue && (
                <Button 
                  onClick={clearTasksSearch}
                  variant="outline"
                  className="px-6"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select value={tasksEntriesPerPage.toString()} onValueChange={(val) => setTasksEntriesPerPage(parseInt(val))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background border shadow-md">
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">Entries</span>
            </div>
          </div>

          {tasksLoading && (
            <div className="flex justify-center py-8">
              <LoaderComponent />
            </div>
          )}

          {tasksError && (
            <div className="text-center py-8">
              <p className="text-destructive">{tasksError}</p>
            </div>
          )}

          {!tasksLoading && !tasksError && (
            <>
              <div id="table-admin-workflow-tasks-container" className="rounded-md border">
                <Table id="table-admin-workflow-tasks">
                  <TableHeader>
                    <TableRow id="table-admin-workflow-tasks-header-row">
                      <TableHead id="header-cell-task-name">Task Name</TableHead>
                      <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody id="table-admin-workflow-tasks-body">
                    {apiTasks.map((task) => (
                      <TableRow key={task.task_id} id={`row-workflow-task-${task.task_id}`}>
                        <TableCell id={`cell-workflow-task-${task.task_id}-name`} className="font-medium">
                          {task.task_name}
                        </TableCell>
                        <TableCell id={`cell-workflow-task-${task.task_id}-actions`} className="text-right">
                          <div className="flex justify-end gap-2">
                            <IconButtonWithTooltip
                              id={`btn-workflow-task-${task.task_id}-history`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleHistory(task)}
                              tooltip="View History"
                              icon={<History className="h-4 w-4" />}
                            />
                            <IconButtonWithTooltip
                              id={`btn-workflow-task-${task.task_id}-edit`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(task.task_id)}
                              tooltip="Edit"
                              icon={<Edit className="h-4 w-4" />}
                            />
                            <IconButtonWithTooltip
                              id={`btn-workflow-task-${task.task_id}-delete`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(task.task_id)}
                              className="text-destructive hover:text-destructive"
                              tooltip="Delete"
                              icon={<Trash2 className="h-4 w-4" />}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {apiTasks.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No tasks found.</p>
                </div>
              )}

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground text whitespace-nowrap">
                  Showing {((tasksCurrentPage - 1) * tasksEntriesPerPage) + 1} to {Math.min(tasksCurrentPage * tasksEntriesPerPage, tasksTotalCount)} of {tasksTotalCount} entries
                </div>
                
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setTasksCurrentPage(tasksCurrentPage - 1)}
                        className={tasksCurrentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    {Array.from({ length: Math.min(5, tasksTotalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink 
                            onClick={() => setTasksCurrentPage(pageNum)}
                            className={pageNum === tasksCurrentPage 
                              ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                              : "hover:bg-purple-100 cursor-pointer"
                            }
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setTasksCurrentPage(tasksCurrentPage + 1)}
                        className={tasksCurrentPage === tasksTotalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </TabsContent>
        </Tabs>

        <OperationsHistoryModal
          open={isHistoryModalOpen}
          onOpenChange={(open) => {
            setIsHistoryModalOpen(open);
            if (!open) {
              setSelectedWorkflowId(null);
              setSelectedWorkflowStatusId(null);
              setSelectedTaskId(null);
            }
          }}
          entityName={
            selectedEntityForHistory?.workflow_name || 
            selectedEntityForHistory?.workflowName || 
            selectedEntityForHistory?.workflow_status ||
            selectedEntityForHistory?.workflowStatusName || 
            selectedEntityForHistory?.task_name || 
            selectedEntityForHistory?.taskName || 
            ""
          }
          entityType={activeTab === "workflow" ? "Workflow" : activeTab === "workflow-status" ? "Workflow Status" : "Task"}
          auditLogs={
            activeTab === "workflow" ? workflowAuditLogs :
            activeTab === "workflow-status" ? workflowStatusAuditLogs :
            activeTab === "tasks" ? taskAuditLogs :
            undefined
          }
          loading={
            activeTab === "workflow" ? auditLogsLoading :
            activeTab === "workflow-status" ? workflowStatusAuditLogsLoading :
            activeTab === "tasks" ? taskAuditLogsLoading :
            false
          }
        />

        <AlertDialog open={isDeleteDialogOpen} onOpenChange={handleDeleteDialogOpenChange}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                {activeTab === "workflow" && "Are you sure you want to delete this workflow? This action cannot be undone."}
                {activeTab === "workflow-status" && "Are you sure you want to delete this workflow status? This action cannot be undone."}
                {activeTab === "tasks" && "Are you sure you want to delete this Task? This action cannot be undone."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  // Prevent Radix AlertDialog from auto-closing so the loader stays visible
                  e.preventDefault();
                  void handleDelete();
                }}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  };

export default Workflow;